import React, { useState, useEffect } from 'react';
import { Wrench, Settings, AlertTriangle, ArrowLeft, Clock, Package, Monitor, Coffee, Zap } from 'lucide-react';
import API_BASE_URL from "../config";

export default function UnderMaintenancePage() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [floatingTools, setFloatingTools] = useState([]);
  const [progress, setProgress] = useState(0);
  const [websiteStatus, setWebsiteStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch website status from API
    fetchWebsiteStatus();

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Animated progress
    const progressTimer = setInterval(() => {
      setProgress((prev) => (prev >= 95 ? 20 : prev + 1));
    }, 100);

    // Generate floating tools
    const tools = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: 3 + Math.random() * 4,
      delay: Math.random() * 2,
    }));
    setFloatingTools(tools);

    return () => {
      clearInterval(timer);
      clearInterval(progressTimer);
    };
  }, []);

  const fetchWebsiteStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}api/Website/1`);

      if (!response.ok) {
        throw new Error('Failed to fetch website status');
      }

      const data = await response.json();
      setWebsiteStatus(data);
    } catch (err) {
      console.error('Error fetching website status:', err);
      // Set default values if API fails
      setWebsiteStatus({
        estimateTime: '02:00:00',
        mentenance_Details: 'We are currently performing scheduled maintenance to improve your experience.',
        version: 'v2.1.0'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '~2 hours';

    const timeParts = timeString.split(':');
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);

      if (hours > 0 && minutes > 0) {
        return `~${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `~${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      } else if (minutes > 0) {
        return `~${minutes} minutes`;
      }
    }
    return '~2 hours';
  };

  return (
    <div className="min-h-screen  from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'grid-move 20s linear infinite'
        }}></div>
      </div>

      {/* Floating Particles */}
      {floatingTools.map((tool) => (
        <div
          key={tool.id}
          className="absolute opacity-30"
          style={{
            left: `${tool.x}%`,
            top: `${tool.y}%`,
            animation: `float ${tool.duration}s ease-in-out infinite`,
            animationDelay: `${tool.delay}s`,
          }}
        >
          {tool.id % 4 === 0 && <Wrench className="w-6 h-6 text-blue-400" />}
          {tool.id % 4 === 1 && <Package className="w-6 h-6 text-cyan-400" />}
          {tool.id % 4 === 2 && <Settings className="w-6 h-6 text-sky-400" />}
          {tool.id % 4 === 3 && <Zap className="w-6 h-6 text-yellow-400" />}
        </div>
      ))}

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">

        {/* Main Content Card */}
        <div className="bg-gradient-to-br from-white/95 to-blue-50/95 backdrop-blur-xl rounded-3xl shadow-2xl p-12 max-w-5xl w-full border border-blue-200/50 relative overflow-hidden">

          {/* Card Background Effects */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400 rounded-full blur-[120px] opacity-20 animate-pulse-slow"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-[120px] opacity-20 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>

          <div className="relative z-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-full shadow-lg mb-4">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-bold">System Maintenance</span>
              </div>
              <h1 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-600 to-sky-600 mb-4">
                Under Maintenance
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                {loading ? (
                  'Loading maintenance information...'
                ) : (
                  websiteStatus?.mentenance_Details || 'Our engineers are working hard to bring you an enhanced experience'
                )}
              </p>
            </div>

            {/* 3D Human at Computer Animation - Inside Card */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-12 mb-12">

              {/* 3D Human Character */}
              <div className="relative w-80 h-96 perspective-1000">
                <div className="relative w-full h-full preserve-3d animate-float-3d">

                  {/* Human Figure - 3D Style */}
                  <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-48">

                    {/* Head with Face Details */}
                    <div className="relative">
                      <div className="absolute -top-24 left-1/2 -translate-x-1/2 z-20">
                        {/* Head/Face */}
                        <div className="w-20 h-24 relative">
                          {/* Head Shape with Gradient */}
                          <div className="absolute inset-0 bg-gradient-to-br from-amber-200 via-amber-300 to-amber-400 rounded-[50%] shadow-2xl"></div>

                          {/* Hair - Modern Style */}
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-24 h-16">
                            <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-t-[50%] shadow-xl"></div>
                            {/* Hair Details */}
                            <div className="absolute top-2 left-2 w-4 h-6 bg-gray-900 rounded-full opacity-40 blur-sm"></div>
                            <div className="absolute top-2 right-2 w-4 h-6 bg-gray-900 rounded-full opacity-40 blur-sm"></div>
                          </div>

                          {/* Face Features */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            {/* Eyes with Animation */}
                            <div className="flex gap-3 mb-2 mt-2">
                              <div className="relative">
                                <div className="w-3 h-4 bg-white rounded-full shadow-inner"></div>
                                <div className="absolute top-1 left-1 w-2 h-2 bg-gray-900 rounded-full animate-eye-look"></div>
                                <div className="absolute top-1.5 left-1.5 w-1 h-1 bg-white rounded-full"></div>
                              </div>
                              <div className="relative">
                                <div className="w-3 h-4 bg-white rounded-full shadow-inner"></div>
                                <div className="absolute top-1 left-1 w-2 h-2 bg-gray-900 rounded-full animate-eye-look"></div>
                                <div className="absolute top-1.5 left-1.5 w-1 h-1 bg-white rounded-full"></div>
                              </div>
                            </div>

                            {/* Nose */}
                            <div className="w-2 h-3 bg-amber-400 rounded-full mb-1 shadow-sm"></div>

                            {/* Mouth - Animated */}
                            <div className="w-6 h-2 bg-gradient-to-r from-red-400 to-red-500 rounded-full shadow-inner animate-mouth"></div>

                            {/* Glasses */}
                            <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-2">
                              <div className="w-6 h-5 border-2 border-gray-800 rounded-lg bg-blue-100/30"></div>
                              <div className="w-6 h-5 border-2 border-gray-800 rounded-lg bg-blue-100/30"></div>
                              <div className="absolute top-2 left-5 w-2 h-0.5 bg-gray-800"></div>
                            </div>
                          </div>

                          {/* Neck */}
                          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-8 h-4 bg-gradient-to-b from-amber-300 to-amber-400 rounded-b-lg shadow-md"></div>
                        </div>
                      </div>

                      {/* Body - Shirt */}
                      <div className="relative mt-4">
                        <div className="w-32 h-40 mx-auto bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-t-[40%] shadow-2xl relative overflow-hidden">
                          {/* Shirt Details */}
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-full bg-blue-900/30"></div>
                          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-8 bg-gradient-to-b from-white to-gray-200 rounded-full"></div>

                          {/* Pocket */}
                          <div className="absolute top-6 left-6 w-6 h-6 border-2 border-blue-900 rounded-sm"></div>

                          {/* Arms */}
                          <div className="absolute top-6 -left-8 w-10 h-32 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full shadow-xl transform -rotate-45 origin-top">
                            {/* Hand reaching to keyboard */}
                            <div className="absolute bottom-0 right-0 w-6 h-6 bg-gradient-to-br from-amber-200 to-amber-300 rounded-full shadow-lg"></div>
                          </div>
                          <div className="absolute top-6 -right-8 w-10 h-32 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full shadow-xl transform rotate-12 origin-top animate-typing-arm">
                            {/* Hand on mouse */}
                            <div className="absolute bottom-0 left-0 w-6 h-6 bg-gradient-to-br from-amber-200 to-amber-300 rounded-full shadow-lg"></div>
                          </div>
                        </div>

                        {/* Legs */}
                        <div className="flex gap-3 justify-center mt-2">
                          <div className="w-14 h-24 bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 rounded-b-2xl shadow-xl"></div>
                          <div className="w-14 h-24 bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 rounded-b-2xl shadow-xl"></div>
                        </div>
                      </div>

                      {/* Chair */}
                      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 -z-10">
                        <div className="w-36 h-8 bg-gradient-to-b from-gray-600 to-gray-700 rounded-t-3xl shadow-xl"></div>
                        <div className="w-3 h-12 bg-gradient-to-b from-gray-700 to-gray-800 mx-auto rounded-b-lg shadow-lg"></div>
                      </div>
                    </div>
                  </div>

                  {/* Computer Setup */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full">

                    {/* Monitor */}
                    <div className="absolute -right-8 bottom-24 z-10">
                      <div className="relative">
                        {/* Monitor Frame */}
                        <div className="w-40 h-28 bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-lg shadow-2xl border-4 border-gray-700 overflow-hidden">
                          {/* Screen */}
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 via-cyan-500 to-sky-400 p-2">
                            {/* Code Lines */}
                            <div className="space-y-1 animate-code-scroll">
                              <div className="h-1 bg-green-400 rounded w-3/4 shadow-glow-green"></div>
                              <div className="h-1 bg-blue-300 rounded w-1/2 shadow-glow-blue"></div>
                              <div className="h-1 bg-yellow-400 rounded w-2/3 shadow-glow-yellow"></div>
                              <div className="h-1 bg-cyan-300 rounded w-5/6 shadow-glow-cyan"></div>
                              <div className="h-1 bg-green-400 rounded w-1/3 shadow-glow-green"></div>
                              <div className="h-1 bg-sky-300 rounded w-3/4 shadow-glow-sky"></div>
                              <div className="h-1 bg-blue-300 rounded w-1/2 shadow-glow-blue"></div>
                            </div>

                            {/* Loading Indicator */}
                            <div className="absolute bottom-2 right-2 flex gap-1">
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-glow-green"></div>
                              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse shadow-glow-yellow" style={{ animationDelay: '0.2s' }}></div>
                              <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse shadow-glow-red" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                          </div>
                        </div>

                        {/* Monitor Stand */}
                        <div className="w-4 h-8 bg-gradient-to-b from-gray-700 to-gray-900 mx-auto rounded-b-lg shadow-xl"></div>
                        <div className="w-16 h-2 bg-gradient-to-r from-gray-800 to-gray-900 mx-auto rounded-lg shadow-xl"></div>
                      </div>
                    </div>

                    {/* Desk */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-72 h-4 bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 rounded-lg shadow-2xl">
                      {/* Desk Edge */}
                      <div className="absolute -top-1 inset-x-0 h-2 bg-gradient-to-r from-amber-600 to-amber-700 rounded-t-lg"></div>
                    </div>

                    {/* Keyboard */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 -translate-x-8 z-20">
                      <div className="w-24 h-8 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-lg shadow-xl border border-gray-600">
                        <div className="grid grid-cols-8 gap-0.5 p-1">
                          {[...Array(24)].map((_, i) => (
                            <div key={i} className="h-1 bg-gradient-to-b from-gray-600 to-gray-700 rounded-sm shadow-inner animate-key-press" style={{ animationDelay: `${i * 0.1}s` }}></div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Mouse */}
                    <div className="absolute bottom-4 right-16 z-20">
                      <div className="w-6 h-9 bg-gradient-to-br from-gray-700 to-gray-900 rounded-t-2xl rounded-b-lg shadow-xl border border-gray-600 animate-mouse-click">
                        <div className="w-0.5 h-4 bg-gray-600 mx-auto mt-1"></div>
                      </div>
                    </div>

                    {/* Coffee Cup */}
                    <div className="absolute bottom-4 -right-16 z-20 animate-float">
                      <div className="relative">
                        <div className="w-10 h-12 bg-gradient-to-br from-red-600 to-red-800 rounded-b-lg border-2 border-red-900 shadow-xl">
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-2 bg-red-700 rounded-t-lg"></div>
                        </div>
                        <div className="absolute -right-2 top-3 w-4 h-6 border-2 border-red-800 rounded-r-full"></div>
                        {/* Steam */}
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <div className="w-1 h-6 bg-gray-400 blur-sm opacity-60 animate-steam"></div>
                        </div>
                        <Coffee className="absolute top-2 left-1/2 -translate-x-1/2 w-4 h-4 text-amber-900" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Section */}
              <div className="flex-1 space-y-6 max-w-md">
                {/* Progress Bar */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-gray-700">Maintenance Progress</span>
                    <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
                      {progress}%
                    </span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 via-cyan-600 to-sky-600 rounded-full transition-all duration-300 shadow-glow-blue"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                    <Zap className="w-4 h-4 text-yellow-500 animate-pulse" />
                    <span>System optimization in progress...</span>
                  </div>
                </div>

                {/* Current Time & Version */}
                <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-6 shadow-xl text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-6 h-6" />
                    <span className="text-sm font-semibold opacity-90">Current Time</span>
                  </div>
                  <div className="text-4xl font-black mb-2">
                    {currentTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </div>
                  <div className="text-sm opacity-80 mb-3">
                    Estimated completion: {formatTime(websiteStatus?.estimateTime)}
                  </div>
                  <div className="flex items-center gap-2 text-sm opacity-90 bg-white/20 rounded-lg px-3 py-2">
                    <Package className="w-4 h-4" />
                    <span>Version: {websiteStatus?.version || 'v2.1.0'}</span>
                  </div>
                </div>

                {/* Status */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-blue-200">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-glow-green"></div>
                      <span className="text-sm text-gray-700">Database optimization</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse shadow-glow-yellow"></div>
                      <span className="text-sm text-gray-700">Server updates</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shadow-glow-blue"></div>
                      <span className="text-sm text-gray-700">Cache clearing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/'}
                className="group flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                Go to Homepage
              </button>
              <button
                onClick={() => window.location.reload()}
                className="group flex items-center justify-center gap-3 bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <Settings className="w-5 h-5 animate-spin-slow group-hover:animate-spin" />
                Refresh Page
              </button>
            </div>

            {/* Footer Info */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm mb-2">Need immediate assistance?</p>
              <a href="mailto:support@prpo.com" className="text-blue-600 font-bold hover:text-blue-700 transition-colors inline-flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                support@prpo.com
              </a>
            </div>

            {/* Hidden Admin Control - Bottom Right */}
      <div className="absolute bottom-4 right-4">
  <label className="flex items-center gap-2 cursor-pointer group bg-transparent">
    <input
      type="checkbox"
      checked={websiteStatus?.website_Status || false}
      onChange={async (e) => {
        const newStatus = e.target.checked;
        try {
          const response = await fetch(`${API_BASE_URL}api/Website/1`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              Id: 1,
              Website_Status: newStatus,
              EstimateTime: websiteStatus?.estimateTime,
              Mentenance_Details: websiteStatus?.mentenance_Details,
              Version: websiteStatus?.version
            })
          });

          if (response.ok) {
            const data = await response.json();
            setWebsiteStatus(data);

            if (newStatus) {
              setTimeout(() => (window.location.href = '/'), 500);
            }
          }
        } catch (err) {
          console.error('Error updating status:', err);
        }
      }}
      className="
        w-4 h-4
        appearance-none
        bg-transparent
        border border-white/60
        rounded
        cursor-pointer
        focus:outline-none
        focus:ring-0
        checked:bg-transparent
        checked:border-white
      "
    />
    <span className="text-xs text-white/70 group-hover:text-white transition select-none">
      Admin
    </span>
  </label>
</div>


          </div>
        </div>
      </div>

      {/* Enhanced CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(10deg);
          }
        }

        @keyframes float-3d {
          0%, 100% {
            transform: translateY(0px) rotateY(0deg);
          }
          50% {
            transform: translateY(-10px) rotateY(5deg);
          }
        }

        @keyframes eye-look {
          0%, 100% {
            transform: translate(0, 0);
          }
          25% {
            transform: translate(1px, 0);
          }
          75% {
            transform: translate(-1px, 0);
          }
        }

        @keyframes mouth {
          0%, 100% {
            transform: scaleX(1);
          }
          50% {
            transform: scaleX(1.1);
          }
        }

        @keyframes typing-arm {
          0%, 100% {
            transform: rotate(12deg);
          }
          50% {
            transform: rotate(18deg);
          }
        }

        @keyframes code-scroll {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-10px);
          }
        }

        @keyframes key-press {
          0%, 100% {
            transform: translateY(0);
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          50% {
            transform: translateY(1px);
            box-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
        }

        @keyframes mouse-click {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(1px);
          }
        }

        @keyframes steam {
          0% {
            transform: translateY(0) scaleX(1);
            opacity: 0.6;
          }
          100% {
            transform: translateY(-20px) scaleX(1.5);
            opacity: 0;
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.3;
          }
        }

        @keyframes grid-move {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(50px);
          }
        }

        .perspective-1000 {
          perspective: 1000px;
        }

        .preserve-3d {
          transform-style: preserve-3d;
        }

        .animate-float-3d {
          animation: float-3d 6s ease-in-out infinite;
        }

        .animate-eye-look {
          animation: eye-look 4s ease-in-out infinite;
        }

        .animate-mouth {
          animation: mouth 3s ease-in-out infinite;
        }

        .animate-typing-arm {
          animation: typing-arm 1s ease-in-out infinite;
        }

        .animate-code-scroll {
          animation: code-scroll 2s ease-in-out infinite;
        }

        .animate-key-press {
          animation: key-press 2s ease-in-out infinite;
        }

        .animate-mouse-click {
          animation: mouse-click 1s ease-in-out infinite;
        }

        .animate-steam {
          animation: steam 2s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }

        .shadow-glow-green {
          box-shadow: 0 0 10px rgba(74, 222, 128, 0.5);
        }

        .shadow-glow-blue {
          box-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
        }

        .shadow-glow-yellow {
          box-shadow: 0 0 10px rgba(250, 204, 21, 0.5);
        }

        .shadow-glow-cyan {
          box-shadow: 0 0 10px rgba(34, 211, 238, 0.5);
        }

        .shadow-glow-sky {
          box-shadow: 0 0 10px rgba(125, 211, 252, 0.5);
        }

        .shadow-glow-pink {
          box-shadow: 0 0 10px rgba(244, 114, 182, 0.5);
        }

        .shadow-glow-purple {
          box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
        }

        .shadow-glow-red {
          box-shadow: 0 0 10px rgba(248, 113, 113, 0.5);
        }

        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}